/**
 * SandboxPanel
 *
 * A unified, configurable UI panel for the complete sandbox lifecycle.
 * Works with `useSandboxLifecycle` to render the correct UI for each state:
 *
 *   none/error  → Launch button + T&C checkbox
 *   provisioning → Spinner + progress bar + Cancel button
 *   stopping     → Red spinner + "Stopping..." text
 *   active       → "Open Jupyter" button + "Stop Sandbox" button
 *   error        → Error card with Retry + Contact Admin
 *
 * Usage:
 *   <SandboxPanel
 *     lifecycle={lifecycle}
 *     onOpenSandbox={handleOpenSandbox}
 *     projectParams={project}
 *     agreed={agreed}
 *     setAgreed={setAgreed}
 *   />
 */

import React, { useState } from "react";
import { Button } from "../ui/button";
import { Card, CardContent } from "../ui/card";
import { Progress } from "../ui/progress";
import {
  RefreshCw,
  ExternalLink,
  Power,
  Play,
  AlertCircle,
  MessageSquare,
  Info,
  Loader2,
} from "lucide-react";
import { toast } from "sonner";
import SandboxTermsModal from "../SandboxTerms";
import { useAuth } from "../../App";
import { useJupyterWorkspace } from "../../contexts/JupyterWorkspaceContext";
import { useAgentSessionStatusQuery } from "../../api/jupyterManagementApi";
import type { SandboxLifecycleReturn, SandboxStatus } from "../../hooks/useSandboxLifecycle";

// ─── Types ─────────────────────────────────────────────────────────────────────

export interface SandboxPanelProps {
  /** Return value of useSandboxLifecycle */
  lifecycle: SandboxLifecycleReturn;

  /** Navigate to the Jupyter notebook view */
  onOpenSandbox: () => void;

  /** Project or submission object — used for agent status check (local/cloud toggle) */
  projectParams?: { id?: string; [key: string]: any };

  /** Terms & conditions agreement state */
  agreed: boolean;
  setAgreed: (v: boolean) => void;

  /** Optional: disable launch and show explanation */
  launchDisabledReason?: string;

  /** Custom label for the launch button */
  launchLabel?: string;

  /** Custom hover message for launch button */
  launchHoverMessage?: string;

  /** Called when user clicks Retry after an error */
  onRetry?: () => void;

  /** Called when user clicks Contact Admin */
  onContactAdmin?: () => void;

  /** Estimated provisioning time shown in the progress card */
  estimatedTime?: string;
}

// ─── Sub-components ────────────────────────────────────────────────────────────

function ProvisioningCard({
  sandboxStatus,
  onCancel,
  estimatedTime,
}: {
  sandboxStatus: SandboxStatus;
  onCancel: () => void;
  estimatedTime?: string;
}) {
  const isStopping = sandboxStatus.status === "stopping";

  return (
    <Card className={isStopping ? "border-red-200 bg-red-50" : "border-blue-200 bg-blue-50"}>
      <CardContent className="p-6">
        <div className="space-y-4">
          <div className="flex items-center space-x-3">
            <RefreshCw
              className={`h-6 w-6 animate-spin ${isStopping ? "text-red-600" : "text-blue-600"}`}
            />
            <div>
              <h3 className={`font-medium ${isStopping ? "text-red-900" : "text-blue-900"}`}>
                {isStopping ? "Stopping Sandbox Environment" : "Provisioning Sandbox Environment"}
              </h3>
              <p className={`text-sm ${isStopping ? "text-red-700" : "text-blue-700"}`}>
                {isStopping
                  ? "Gracefully shutting down your environment..."
                  : "Setting up your dedicated Jupyter notebook environment..."}
              </p>
            </div>
          </div>

          {!isStopping && (
            <div className="space-y-2">
              <div className="flex justify-between text-sm">
                <span className="text-blue-700">Progress</span>
                <span className="text-blue-700">
                  {Math.floor(sandboxStatus.provisioningProgress || 0)}%
                </span>
              </div>
              <Progress value={Math.floor(sandboxStatus.provisioningProgress || 0)} className="h-3" />
            </div>
          )}

          <div className="flex items-center justify-between">
            {!isStopping && (
              <div className="text-sm text-blue-700">
                Estimated time: {estimatedTime || sandboxStatus.estimatedTime || "2–3 minutes"}
              </div>
            )}
            {!isStopping && (
              <Button
                variant="ghost"
                size="sm"
                className="text-red-600 hover:text-red-700 hover:bg-red-50 h-8"
                onClick={onCancel}
              >
                Cancel Launch
              </Button>
            )}
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

function InitializationFailureCard({
  errorMessage,
  onRetry,
  onContactAdmin,
}: {
  errorMessage?: string;
  onRetry: () => void;
  onContactAdmin: () => void;
}) {
  return (
    <Card className="border-red-200 bg-red-50 overflow-hidden shadow-sm">
      <div className="h-1.5 bg-red-500 w-full" />
      <CardContent className="p-6">
        <div className="flex items-start gap-4">
          <div className="p-3 bg-red-100 rounded-full flex-shrink-0">
            <AlertCircle className="h-6 w-6 text-red-600" />
          </div>
          <div className="space-y-4 flex-1">
            <div>
              <h3 className="text-lg font-bold text-red-900 leading-tight">
                Workspace Initialization Failed
              </h3>
              <p className="text-red-700 text-sm mt-1">
                Something went wrong while setting up your environment.
              </p>
            </div>
            <div className="bg-white/80 border border-red-100 rounded-lg p-3">
              <p className="text-xs font-mono text-red-800 break-words">
                {errorMessage || "Internal initialization error occurred."}
              </p>
            </div>
            <p className="text-xs text-red-600 italic">
              Please try again or contact your course administrator if the issue persists.
            </p>
            <div className="flex gap-3 pt-2">
              <Button
                onClick={onRetry}
                className="bg-red-600 hover:bg-red-700 text-white border-none shadow-sm h-9"
              >
                <RefreshCw className="h-4 w-4 mr-2" />
                Retry
              </Button>
              <Button
                variant="outline"
                className="border-red-200 text-red-700 hover:bg-red-100 h-9"
                onClick={onContactAdmin}
              >
                <MessageSquare className="h-4 w-4 mr-2" />
                Contact Admin
              </Button>
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

function StoppingIndicator() {
  return (
    <div className="flex items-center gap-2 bg-red-50 px-4 py-3 rounded-md border border-red-200">
      <Loader2 className="h-4 w-4 text-red-600 animate-spin" />
      <div>
        <p className="text-sm font-medium text-red-900">Stopping...</p>
        <p className="text-xs text-red-600">Removing Environment</p>
      </div>
    </div>
  );
}

// ─── SandboxPanel (main export) ────────────────────────────────────────────────

export function SandboxPanel({
  lifecycle,
  onOpenSandbox,
  projectParams,
  agreed,
  setAgreed,
  launchDisabledReason,
  launchLabel = "Launch Sandbox",
  launchHoverMessage,
  onRetry,
  onContactAdmin,
  estimatedTime,
}: SandboxPanelProps) {
  const { sandboxStatus, handleLaunch, handleCancel, handleStop } = lifecycle;

  const [termsOpen, setTermsOpen] = useState(false);
  const { user: currentUser } = useAuth();
  const { workspace, updateWorkspace } = useJupyterWorkspace();
  const isAdmin = currentUser?.role === "admin";

  // Agent status for local/cloud toggle (only relevant when admin + local mode)
  const projectId = projectParams?.id;
  const { data: agentSessionStatus } = useAgentSessionStatusQuery(
    projectId || "",
    workspace.globalSandboxMode === "local" && workspace.workspaceType === "local"
  );
  const agentStatus = agentSessionStatus?.status || "SLEEPING";

  const toggleLocalMode = (checked: boolean) => {
    updateWorkspace({ workspaceType: checked ? "local" : "cloud" });
    toast.success(`Switched to ${checked ? "LOCAL" : "CLOUD"} gateway`);
  };

  const isLocalAgentRequired = workspace.globalSandboxMode === "local" && workspace.workspaceType === "local";
  const isAgentOffline = isLocalAgentRequired && agentStatus !== "CONNECTED";
  const isLaunchBlocked = (!!launchDisabledReason || isAgentOffline) && sandboxStatus.status !== "active";

  // ── stopping ──────────────────────────────────────────────────────────────────
  if (sandboxStatus.status === "stopping") {
    return <StoppingIndicator />;
  }

  // ── provisioning ──────────────────────────────────────────────────────────────
  if (sandboxStatus.status === "provisioning") {
    return (
      <ProvisioningCard
        sandboxStatus={sandboxStatus}
        onCancel={handleCancel}
        estimatedTime={estimatedTime}
      />
    );
  }

  // ── active ────────────────────────────────────────────────────────────────────
  if (sandboxStatus.status === "active") {
    return (
      <div className="space-y-2">
        <Button
          className="w-full bg-green-600 hover:bg-green-700"
          onClick={onOpenSandbox}
        >
          <ExternalLink className="h-4 w-4 mr-2" />
          Open Jupyter Notebook
        </Button>
        <Button
          variant="destructive"
          className="w-full"
          onClick={handleStop}
        >
          <Power className="h-4 w-4 mr-2" />
          Stop Sandbox
        </Button>
      </div>
    );
  }

  // ── none / error ──────────────────────────────────────────────────────────────
  return (
    <div className="space-y-3">
      {/* Admin local/cloud mode toggle */}
      {isAdmin && workspace.globalSandboxMode === "local" && (
        <div
          className="flex items-center justify-between bg-gray-50 p-3 rounded-lg border border-dashed text-xs mb-2 cursor-pointer hover:bg-gray-100 transition-colors"
          onClick={() => toggleLocalMode(workspace.workspaceType !== "local")}
        >
          <div className="flex flex-col">
            <span className="font-semibold text-gray-700">
              {workspace.workspaceType === "local" ? "LOCAL Agent" : "CLOUD Gateway"}
            </span>
            <span className="text-gray-500 text-[10px]">Click to toggle</span>
          </div>
          <div
            className={`px-2 py-0.5 rounded-full text-[10px] font-bold ${
              workspace.workspaceType === "local"
                ? "bg-orange-100 text-orange-700"
                : "bg-blue-100 text-blue-700"
            }`}
          >
            {workspace.workspaceType === "local" ? "LOCAL" : "CLOUD"}
          </div>
        </div>
      )}

      {/* Launch button */}
      <div className="relative group">
        <Button
          className="w-full"
          onClick={handleLaunch}
          disabled={!agreed || isLaunchBlocked}
          title={
            !agreed
              ? "Please agree to the terms and conditions."
              : isAgentOffline
              ? "Local agent is offline. Please run the command in Step 1 first."
              : launchDisabledReason || launchHoverMessage || "Launch a new sandbox."
          }
        >
          {workspace.workspaceType === "local" ? (
            agentStatus === "CONNECTED" ? (
              <>
                <Play className="h-4 w-4 mr-2" />
                {launchLabel}
              </>
            ) : (
              <>
                Waiting for Agent...
              </>
            )
          ) : (
            <>
              <Play className="h-4 w-4 mr-2" />
              {launchLabel}
            </>
          )}
        </Button>

        {isLaunchBlocked && (
          <div className="text-xs text-amber-700 flex items-center gap-1 mt-1">
            <Info className="h-3.5 w-3.5 flex-shrink-0" />
            {launchDisabledReason}
          </div>
        )}
      </div>

      {/* Error card */}
      {sandboxStatus.status === "error" && onRetry && onContactAdmin && (
        <InitializationFailureCard
          errorMessage={sandboxStatus.errorMessage}
          onRetry={onRetry}
          onContactAdmin={onContactAdmin}
        />
      )}

      {/* Terms & Conditions */}
      <div className="flex items-start space-x-2">
        <input
          type="checkbox"
          id="sandbox-panel-agreement"
          checked={agreed}
          onChange={(e) => setAgreed(e.target.checked)}
          className="mt-1"
        />
        <label htmlFor="sandbox-panel-agreement" className="text-sm">
          I understand the provisioning process and associated costs. I agree to the{" "}
          <button
            onClick={() => setTermsOpen(true)}
            className="text-blue-600 text-sm font-bold hover:underline"
          >
            terms and conditions
          </button>
          .
        </label>
      </div>

      <SandboxTermsModal open={termsOpen} onOpenChange={setTermsOpen} />
    </div>
  );
}

export default SandboxPanel;
